/**
 * The practice's own dish list, from the server.
 *
 * One call so far — a page of `GET /api/v1/workspace-foods/`. The endpoint
 * takes `page` and `page_size` and nothing else: there is no search param on
 * it, so this module does no filtering and the screen narrows the page it is
 * holding. See `useDishes`, which is where that is written down.
 *
 * Nothing is stored here. Unlike `plansService` there is no browser-side
 * record to keep in step — the list is whatever the last read said, which is
 * what a module with a real endpoint behind it should be.
 */

import { api, API_ENDPOINTS } from "../../../lib/api";
import type { PaginatedResponse } from "../../../lib/api";
import type { GetToken } from "../../auth/types/auth.types";
import type { Dish, WorkspaceFoodResponse } from "../types/dishes.types";

/** What one page of the table holds, and what the endpoint is asked for. */
export const DEFAULT_PAGE_SIZE = 10;

async function requireToken(getToken: GetToken, action: string): Promise<string> {
  const token = await getToken();
  if (!token) {
    // Not an ApiError: the server was never asked. Calling this without an
    // active session is a bug at the call site, not something to show a user.
    throw new Error(`No active Clerk session - cannot ${action}.`);
  }
  return token;
}

/**
 * A row as the screens read it.
 *
 * Every number is taken at the figure the server sent rather than rounded
 * here: fat comes back as 0.2 for a good many dishes, and a table that rounded
 * at the boundary would print 0 and call it the truth. Rounding is the
 * column's business — see `formatMacro`.
 *
 * Protein is the adjusted figure, not the gross one, which is the same choice
 * `foodsService` makes for a plan row's Prot. column. See
 * `WorkspaceFoodNutritionResponse`.
 */
export const toDish = (row: WorkspaceFoodResponse): Dish => ({
  id: row.id,
  name: row.name,
  baseQuantity: row.base_quantity,
  baseUnit: row.base_unit?.name ?? "",
  macros: {
    calories: row.nutrition?.kcal ?? 0,
    carbs: row.nutrition?.carbs_g ?? 0,
    protein: row.nutrition?.protein_adjusted_g ?? 0,
    fat: row.nutrition?.fat_g ?? 0,
  },
  glycaemicLoad: row.nutrition?.gl ?? 0,
  dietaryPattern: row.dietary_pattern,
  // `?? []` on both: a dish with neither is sent with them empty, but a row
  // that omitted the key entirely would otherwise put `undefined` where the
  // screens map over a list.
  allergens: row.allergens ?? [],
  portions: (row.portions ?? []).map((portion) => ({
    unitId: portion.food_unit.id,
    unit: portion.food_unit.name,
    amountInBaseUnit: portion.amount_in_base_unit,
  })),
  isActive: row.is_active,
  scope: row.scope,
});

export interface DishesPage {
  dishes: Dish[];
  /** How many the workspace holds in all, not how many came back. */
  count: number;
}

export interface FetchDishesOptions {
  /** 1-based, the way the endpoint counts. */
  page?: number;
  pageSize?: number;
  signal?: AbortSignal;
}

/** One page of the list, `GET /api/v1/workspace-foods/?page=&page_size=`. */
export async function fetchDishesPage(
  getToken: GetToken,
  { page = 1, pageSize = DEFAULT_PAGE_SIZE, signal }: FetchDishesOptions = {}
): Promise<DishesPage> {
  const token = await requireToken(getToken, "load your dishes");

  const response = await api.get<PaginatedResponse<WorkspaceFoodResponse>>(
    API_ENDPOINTS.workspaceFoods.list,
    { token, query: { page, page_size: pageSize }, signal }
  );

  return {
    dishes: (response?.results ?? []).map(toDish),
    // `?? 0` rather than the row count: an envelope without a count is a
    // malformed answer, and guessing the total from one page would tell the
    // pagination there is only ever one.
    count: response?.count ?? 0,
  };
}
